/**
 * La barre de chronomètres de la séance (`ARCHITECTURE_V2.md` §4.5,
 * specs §12.3).
 *
 * 🔴 **C'est une surface OPAQUE ordinaire, pas une surface de verre.** Le §4.5
 * l'annonçait comme « une quatrième surface de verre » à déclarer dans le
 * registre de `glass.css` — cette phrase est **datée** : le Liquid Glass a été
 * entièrement déposé le 25/08/2026 après dix jours d'usage (règle non
 * négociable n°8), `glass.css` s'appelle `surfaces.css` et ne contient plus
 * aucune couleur. Il n'y a donc plus de registre à alimenter, et le danger que
 * ce registre existait pour prévenir — Safari repeignant le verre par-dessus
 * les feuilles modales — n'existe plus non plus.
 *
 * Elle suit donc le modèle **déjà en service** : celui de l'en-tête collant de
 * la refonte (`.screen-header`), c'est-à-dire `position: sticky` et le fond de
 * page en aplat. Aucun `backdrop-filter`, aucune matière.
 *
 * ⚠️ **`sticky` et non `fixed`** — la raison est celle de `ScreenHeader` : en
 * `fixed` il faudrait réserver sa hauteur à la main et sa largeur cesserait de
 * suivre la colonne de 640 px sur grand écran. En `sticky`, il n'y a rien à
 * synchroniser.
 *
 * ⚠️ **Elle se colle SOUS l'en-tête**, pas en haut de la fenêtre : les deux
 * sont collants et le second doit s'arrêter au bas du premier, sinon ils se
 * chevauchent au défilement. C'est ce que fait `top: var(--session-bar-top)`
 * dans `nourri-screens.css`.
 */

import { formatClock, type RestTimer } from '../../hooks/useSessionClock';

/**
 * ⚠️ **Deux chronomètres, deux registres de lecture.** Celui de la séance est
 * une information de contexte, qu'on consulte de temps en temps ; celui du
 * repos est ce qu'on **attend**, et c'est lui qui doit sauter aux yeux depuis
 * un banc, téléphone posé à un mètre. D'où la hiérarchie : le repos en grand,
 * la séance en petit à côté.
 *
 * ⚠️ **Le repos garde sa place quand il ne court pas**, occupée par le total de
 * séries faites. Une barre dont la moitié apparaît et disparaît toutes les
 * deux minutes ferait sauter le reste de l'écran à chaque série — et c'est très
 * exactement le défaut que l'étape 5 a corrigé sur la pastille de
 * synchronisation (« un contrôle qui se déplace est un contrôle qu'on rate »).
 */
export function SessionTimers({
  elapsed,
  rest,
  done,
  total,
  onSkipRest,
}: {
  elapsed: number;
  rest: RestTimer;
  done: number;
  total: number;
  onSkipRest: () => void;
}) {
  const enRepos = rest.remaining !== null;

  return (
    /*
      🔴 **Deux éléments, et c'est ce qui fait disparaître les exercices AVANT
      la barre** *(31/08/2026, relevé sur l'appareil : « les blocs des exos qui
      passent en dessous, rend bizarre »)*.

      La barre était une **carte arrondie** collante, posée dans les gouttières
      de `.app-main` (1 rem + zone sûre). Le contenu qui défile passait donc
      dessous **et se voyait** : de part et d'autre dans les gouttières, et dans
      les quatre angles arrondis. Un fond opaque ne suffisait pas — il ne
      couvrait que la carte, pas la largeur de l'écran.

      D'où la séparation des deux rôles, exactement celle du pager
      (`.day-viewport` découpe, `.day-track` bouge) : la **bande** occupe toute
      la largeur et masque, la **carte** ne fait plus que porter la forme. Une
      seule boîte ne pouvait pas être à la fois pleine largeur et arrondie en
      retrait.
    */
    <div className="n-session-bar">
      <div className="n-session-bar__inner">
      <div className="n-session-bar__slot">
        <span className="n-session-bar__label">Séance</span>
        <span className="n-session-bar__value tnum">{formatClock(elapsed)}</span>
      </div>

      {enRepos ? (
        <button
          type="button"
          /*
            ⚠️ **Le repos dépassé se signale par le mot ET par la teinte**, comme
            « restant » / « de trop » sur le bandeau du journal : `--ok` et
            `--danger` ne sont qu'à ΔE 2,2 en vision déficiente, et ne sont
            légaux que parce que le texte double la couleur. Ne pas retirer
            l'encodage secondaire.
          */
          className={rest.overdue ? 'n-session-bar__rest is-overdue' : 'n-session-bar__rest'}
          onClick={onSkipRest}
        >
          <span className="n-session-bar__label">
            {rest.overdue ? 'Repos dépassé' : 'Repos'}
          </span>
          <span className="n-session-bar__value n-session-bar__value--big tnum">
            {formatClock(rest.remaining ?? 0)}
          </span>
        </button>
      ) : (
        <div className="n-session-bar__slot n-session-bar__slot--end">
          <span className="n-session-bar__label">Séries</span>
          <span className="n-session-bar__value tnum">
            {String(done)} / {String(total)}
          </span>
        </div>
      )}
      </div>
    </div>
  );
}
